import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/layout/Navbar";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { logger } from "@/lib/logger";

const MAX_ATTEMPTS = 12;
const POLL_DELAY_MS = 2500;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("pending"); // pending | paid | failed | expired
  const attemptsRef = useRef(0);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);

  // Stripe returns `session_id`; PayPal auto-appends `token=<order_id>`.
  const sessionId = params.get("session_id") || params.get("token") || params.get("paypal_order_id");

  const poll = useCallback(async () => {
    if (cancelledRef.current) return;
    try {
      const r = await api.get(`/checkout/status/${sessionId}`);
      if (cancelledRef.current) return;
      if (r.data.payment_status === "paid") {
        setStatus("paid");
        await refresh();
        return;
      }
      if (r.data.status === "expired") { setStatus("expired"); return; }
      attemptsRef.current += 1;
      if (attemptsRef.current > MAX_ATTEMPTS) { setStatus("failed"); return; }
      timerRef.current = setTimeout(poll, POLL_DELAY_MS);
    } catch (err) {
      logger.error("[payment] status check failed", err);
      attemptsRef.current += 1;
      if (attemptsRef.current > 4) setStatus("failed");
      else timerRef.current = setTimeout(poll, POLL_DELAY_MS);
    }
  }, [sessionId, refresh]);

  useEffect(() => {
    cancelledRef.current = false;
    attemptsRef.current = 0;
    if (!sessionId) { setStatus("failed"); return undefined; }
    poll();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId, poll]);

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-[700px] mx-auto px-6 py-20 text-center">
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-klein" />
            <div className="overline mt-6">// Payment received</div>
            <h1 className="font-display font-black text-5xl uppercase mt-3 tracking-tighter">Welcome aboard.</h1>
            <p className="text-muted-foreground mt-4">Your credits / subscription is now active. Go unlock those leads.</p>
            <button onClick={() => navigate("/contractor")} className="brut-btn-primary mt-8" data-testid="success-go-dashboard">Go to dashboard</button>
          </>
        )}
        {status === "pending" && (
          <>
            <div className="font-mono uppercase tracking-widest text-sm">Confirming your payment…</div>
            <div className="overline mt-4">// please wait</div>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <AlertCircle className="w-16 h-16 mx-auto text-destructive" />
            <h1 className="font-display font-black text-4xl uppercase mt-6 tracking-tighter">Payment {status}</h1>
            <p className="text-muted-foreground mt-4">No worries. Try again from the pricing page.</p>
            <button onClick={() => navigate("/pricing")} className="brut-btn-primary mt-8" data-testid="retry-pricing">Back to pricing</button>
          </>
        )}
      </div>
    </div>
  );
}
